import React, { useState } from 'react';

import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CheckboxField from 'components/application/CheckboxField';
import RadioField from 'components/application/RadioField';
import SidePanel from 'components/application/SidePanel';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import FormFooter from 'components/sidepanels/FormFooter';
import TrainingStatus from 'components/training/TrainingStatus';

export default function TrainingReportSidePanel({
  courses,
  isOpen,
  isReportGenerating,
  onClose,
  onSubmit,
}) {
  const { trainingReportPermittedOptions } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingReportSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const orderedDisabledCourses = courses
    .filter((course) => course.isDisabled)
    .sort((a, b) => a.name.localeCompare(b.name));

  const orderedCourses = courses
    .filter((course) => !course.isDisabled)
    .sort((a, b) => a.name.localeCompare(b.name));

  const orderedCoursesForTrainingReport = [
    ...orderedCourses,
    ...orderedDisabledCourses,
  ];

  const defaultSelectedCourseIds = orderedCourses.map((course) => course.id);

  const [selectedAdditionalReportOptions, setSelectedAdditionalReportOptions] =
    useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState(
    defaultSelectedCourseIds,
  );
  const [courseSelectionMode, setCourseSelectionMode] = useState('includeAll');

  const handleAdditionalOptionsChange = (event) => {
    const option = event.target.value;
    const isChecked = event.target.checked;
    setSelectedAdditionalReportOptions((prev) => {
      if (isChecked) {
        return prev.includes(option) ? prev : [...prev, option];
      } else {
        return prev.filter((item) => item !== option);
      }
    });
  };

  const handleCourseSelectionChange = (event) => {
    const courseId = event.target.value;
    const isChecked = event.target.checked;
    setSelectedCourseIds((prev) => {
      if (isChecked) {
        return prev.includes(courseId) ? prev : [...prev, courseId];
      } else {
        return prev.filter((id) => id !== courseId);
      }
    });
  };

  const handleCourseSelectionModeChange = (event) => {
    const newMode = event.target.value;
    setCourseSelectionMode(newMode);

    if (newMode === 'selectCourses') {
      setSelectedCourseIds(defaultSelectedCourseIds);
    }
  };

  const handleReset = () => {
    setCourseSelectionMode('includeAll');
    setSelectedCourseIds(defaultSelectedCourseIds);
    setSelectedAdditionalReportOptions([]);
  };

  const handleSubmit = () => {
    const isAllCoursesSelected =
      courseSelectionMode === 'includeAll' ||
      _.isEqual(selectedCourseIds, defaultSelectedCourseIds);

    const additionalOptionsAndCoursesParams = {
      ...selectedAdditionalReportOptions.reduce((acc, option) => {
        acc[option] = true;
        return acc;
      }, {}),
    };

    if (!isAllCoursesSelected) {
      additionalOptionsAndCoursesParams.course_ids = selectedCourseIds;
    }

    onSubmit({ additionalOptionsAndCoursesParams });

    handleReset();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const isAtLeastOneCourseSelected =
    courseSelectionMode === 'includeAll' || selectedCourseIds.length > 0;

  const checkboxProps = {
    height: 'short',
    onChange: handleAdditionalOptionsChange,
    className: 'tw-mb-3',
  };

  return (
    <SidePanel
      bodyContent={
        <>
          <div
            className='tw-p-6'
            data-element-name='additional-options-section'
          >
            <div className='tw-mb-4 tw-text-l tw-font-semibold'>
              {i18nT('description')}
            </div>
            {trainingReportPermittedOptions.includes('notes') && (
              <CheckboxField
                {...checkboxProps}
                checked={selectedAdditionalReportOptions.includes('notes')}
                label={i18nTCommon('shared.notes')}
                labelTextProps={{ className: 'tw-font-normal tw-text-m' }}
                name='notes'
                value='notes'
              />
            )}
            {trainingReportPermittedOptions.includes('evidence') && (
              <CheckboxField
                {...checkboxProps}
                checked={selectedAdditionalReportOptions.includes('evidence')}
                label={i18nTCommon('shared.evidence')}
                labelTextProps={{ className: 'tw-font-normal tw-text-m' }}
                name='evidence'
                value='evidence'
              />
            )}
            {trainingReportPermittedOptions.includes('history') && (
              <CheckboxField
                {...checkboxProps}
                checked={selectedAdditionalReportOptions.includes('history')}
                label={i18nT('checkboxFields.history.label')}
                labelProps={{ className: 'hhq-checkbox__label--w-auto' }}
                labelTextProps={{ className: 'tw-font-normal tw-text-m' }}
                name='history'
                tooltip={i18nT('checkboxFields.history.tooltip')}
                value='history'
              />
            )}
          </div>
          <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-200' />
          <div className='tw-p-6'>
            <div className='tw-mb-4 tw-text-l tw-font-semibold'>
              {i18nTCommon('shared.courses')}
            </div>

            <div className='tw-mb-5'>
              <RadioField
                checked={courseSelectionMode === 'includeAll'}
                label={i18nTCommon('shared.includeAll')}
                name='courseSelectionMode'
                onChange={handleCourseSelectionModeChange}
                value='includeAll'
              />
              <RadioField
                checked={courseSelectionMode === 'selectCourses'}
                label={i18nTCommon('shared.selectCourses')}
                name='courseSelectionMode'
                onChange={handleCourseSelectionModeChange}
                value='selectCourses'
              />
            </div>
            {courseSelectionMode === 'selectCourses' && (
              <div
                className='tw-mb-4 tw-ml-6'
                data-element-name='courses-section'
              >
                {orderedCoursesForTrainingReport.map((course) => (
                  <CheckboxField
                    checked={selectedCourseIds.includes(course.id)}
                    className='tw-mb-5'
                    customStyling={course.isRequired ? '' : 'tw-mb-3'}
                    disabled={course.isDisabled}
                    height='short'
                    key={course.id}
                    label={
                      <div className='tw-flex tw-items-start tw-gap-2'>
                        <div className='tw-mt-1 tw-shrink-0'>
                          <TrainingStatus
                            currentTrainingStatus={course.trainingStatus}
                            isRequired={course.isRequired}
                            requiredSource={course.requiredSource}
                          />
                        </div>
                        <div>
                          <span
                            className={classNames(
                              'tw-text-m',
                              'tw-font-normal',
                              !course.isDisabled && 'tw-grey-700',
                            )}
                          >
                            {course.name}
                          </span>
                          {!course.isRequired && (
                            <p className='tw-mb-0 tw-text-xs'>
                              {i18nTCommon('shared.optional')}
                            </p>
                          )}
                        </div>
                      </div>
                    }
                    name={`course-${course.id}`}
                    onChange={handleCourseSelectionChange}
                    tooltip={
                      course.isDisabled ?
                        i18nT('checkboxFields.disabledCourse.tooltip')
                      : null
                    }
                    tooltipType='hover'
                    value={course.id}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      }
      closeCallback={handleClose}
      color='tertiary'
      footerContent={
        <FormFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          onSubmitTooltip={
            (
              !isAtLeastOneCourseSelected &&
              courseSelectionMode === 'selectCourses'
            ) ?
              i18nT('footer.submitButtonDisabledTooltip')
            : null
          }
          submitButtonDisabled={
            isReportGenerating || !isAtLeastOneCourseSelected
          }
          text={i18nTCommon('shared.downloadNow')}
        />
      }
      headerContent={
        <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
          {i18nT('title')}
        </h1>
      }
      isOpen={isOpen}
      onBackdropClick={handleClose}
      scrolledAmount={0}
      setScrolledAmount={() => {}}
      submitDisabled={isReportGenerating}
    />
  );
}

TrainingReportSidePanel.propTypes = {
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      isDisabled: PropTypes.bool,
      isRequired: PropTypes.bool,
      name: PropTypes.string,
      requiredSource: PropTypes.string,
      trainingStatus: PropTypes.string,
    }),
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  isReportGenerating: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
